/**
 * AssetLibraryControls — Header + Suche + Filter-Chips + Grid/Liste-Toggle des
 * AssetLibraryPanel. Aus dem Panel extrahiert (Step-0-Split), damit das Panel
 * unter dem Datei-Limit bleibt. Theme-aware (Mock #10).
 */

import { useTranslation } from 'react-i18next';
import { X, Search, Images, LayoutGrid, List } from 'lucide-react';
import { cn } from '@/lib/utils';

export type SourceFilter = 'all' | 'ai_generated' | 'user_upload' | 'jtl_original' | 'optimized';

const FILTER_OPTIONS: { value: SourceFilter; emoji: string; i18nKey: string }[] = [
  { value: 'all', emoji: '', i18nKey: 'library.panel.filters.all' },
  { value: 'ai_generated', emoji: '✨', i18nKey: 'library.panel.filters.ai' },
  { value: 'user_upload', emoji: '☁️', i18nKey: 'library.panel.filters.upload' },
  { value: 'jtl_original', emoji: '🔄', i18nKey: 'library.panel.filters.jtl' },
  { value: 'optimized', emoji: '⚡', i18nKey: 'library.panel.filters.optimized' },
];

interface AssetLibraryControlsProps {
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  sourceFilter: SourceFilter;
  onFilterChange: (f: SourceFilter) => void;
  showGrouped: boolean;
  setShowGrouped: (v: boolean) => void;
  onClose: () => void;
}

export function AssetLibraryControls(props: AssetLibraryControlsProps) {
  const { t } = useTranslation();
  return (
    <>
      <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
            <Images className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-neutral-900 dark:text-white">{t('library.panel.title')}</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">{t('library.panel.subtitle')}</p>
          </div>
        </div>
        <button
          onClick={props.onClose}
          className="w-8 h-8 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center justify-center text-neutral-500 hover:text-neutral-900 dark:hover:text-white transition-all"
          aria-label={t('common.close')}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="px-5 py-3 border-b border-neutral-200 dark:border-neutral-800">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 dark:text-neutral-500" />
          <input
            type="text"
            placeholder={t('common.search')}
            value={props.searchQuery}
            onChange={(e) => props.setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-neutral-100 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 text-sm text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-transparent transition-all"
          />
        </div>
      </div>

      <div className="px-5 py-3 border-b border-neutral-200 dark:border-neutral-800">
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {FILTER_OPTIONS.map((filter) => (
              <button
                key={filter.value}
                onClick={() => props.onFilterChange(filter.value)}
                className={cn(
                  'px-3 py-1.5 rounded text-sm font-medium transition-all',
                  props.sourceFilter === filter.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700 hover:text-neutral-900 dark:hover:text-white',
                )}
              >
                {filter.emoji ? `${filter.emoji} ` : ''}{t(filter.i18nKey)}
              </button>
            ))}
          </div>
          <div className="flex rounded overflow-hidden border border-neutral-200 dark:border-neutral-700 flex-shrink-0">
            <ViewToggleButton active={!props.showGrouped} onClick={() => props.setShowGrouped(false)} title={t('library.panel.showAll')}>
              <LayoutGrid className="w-4 h-4" />
            </ViewToggleButton>
            <ViewToggleButton active={props.showGrouped} onClick={() => props.setShowGrouped(true)} title={t('library.panel.groupByDate')}>
              <List className="w-4 h-4" />
            </ViewToggleButton>
          </div>
        </div>
      </div>
    </>
  );
}

function ViewToggleButton({ active, onClick, title, children }: { active: boolean; onClick: () => void; title: string; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={cn(
        'p-2 transition-all',
        active ? 'bg-primary-600 text-white' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700',
      )}
    >
      {children}
    </button>
  );
}
